import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, ArrowLeft, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGIONS = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon',
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

const IMPROVEMENTS = [
  'Interior painting', 'Exterior painting', 'Kitchen renovation', 'Cabinet refinishing',
  'Bathroom renovation', 'Flooring', 'Roofing', 'Windows or doors', 'Landscaping',
  'Basement renovation', 'Heating or cooling', 'General repairs', 'Other',
];

const TIMELINES = [
  'As soon as possible', 'Within 3 months', 'Within 6 months', 'Within 12 months',
  'More than one year from now', 'I am only exploring ideas',
];

const INTENT_OPTIONS = [
  'Yes, within the next 3 months', 'Yes, within the next 6 months',
  'Yes, within the next 12 months', 'Yes, within the next 1\u20132 years',
  'Possibly, but I am unsure when', 'No',
];

const OWNERSHIP = [
  'Yes, I am the sole owner', 'Yes, I own it with someone else',
  'I am in the process of purchasing it', 'No, I rent the property',
];

const PROPERTY_TYPES = [
  'Detached house', 'Semi-detached house', 'Townhouse', 'Condominium',
  'Duplex or multi-unit property', 'Other',
];

const HEARD_ABOUT = [
  'Realtor sponsor', 'Contractor', 'Friend or family member', 'Social media',
  'Online advertisement', 'Community organization', 'Other',
];

const STEP_TITLES = [
  'Contact Information', 'Property Information', 'Home Improvement Interests',
  'Real Estate Intent', 'Entry & Communication Consent',
];

const RadioList = ({ value, onChange, options, testPrefix }) => (
  <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
    {options.map((opt, i) => (
      <label
        key={opt}
        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${value === opt ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-neutral-200 hover:border-neutral-300'}`}
      >
        <RadioGroupItem value={opt} data-testid={`${testPrefix}-${i}`} />
        <span className="text-sm text-neutral-800">{opt}</span>
      </label>
    ))}
  </RadioGroup>
);

export default function HomeownerQuestionnaire() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const nameParts = (user?.name || '').split(' ');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: nameParts[0] || '',
    last_name: nameParts.slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    contact_preference: '',
    property_address: '',
    property_city: '',
    property_region: '',
    property_postal_code: '',
    ownership_status: '',
    property_type: '',
    improvement_interests: [],
    project_timeline: '',
    selling_intent: '',
    buying_intent: '',
    heard_about: '',
    sponsor_code: '',
    confirm_accuracy: false,
    agree_entry_communications: false,
    marketing_opt_in: false,
    sponsor_sharing_opt_in: false,
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    axios.get(`${API}/homeowner/questionnaire/status`)
      .then((r) => { if (r.data.completed) navigate('/dashboard'); })
      .catch(() => {});
  }, [navigate]);

  const toggleInterest = (item) =>
    setForm((f) => ({
      ...f,
      improvement_interests: f.improvement_interests.includes(item)
        ? f.improvement_interests.filter((x) => x !== item)
        : [...f.improvement_interests, item],
    }));

  const stepValid = () => {
    switch (step) {
      case 0:
        return form.first_name && form.last_name && form.email && form.phone && form.contact_preference;
      case 1:
        return form.property_address && form.property_city && form.property_region && form.property_postal_code && form.ownership_status && form.property_type;
      case 2:
        return form.improvement_interests.length > 0 && form.project_timeline;
      case 3:
        return form.selling_intent && form.buying_intent;
      case 4:
        return form.heard_about && form.confirm_accuracy && form.agree_entry_communications;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/homeowner/questionnaire`, {
        ...form,
        sponsor_code: form.sponsor_code || null,
      });
      toast.success('Entry submitted! Welcome to Spinser 🎉');
      navigate('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-900/95 flex items-start sm:items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="homeowner-questionnaire"
        className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-6 sm:p-10 my-4"
      >
        <div className="flex items-center gap-3 mb-2">
          <PartyPopper className="w-6 h-6 text-[#FF3B30]" />
          <p className="text-sm font-semibold text-[#FF3B30] uppercase tracking-wider">Account created!</p>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-1" style={{ fontFamily: 'Outfit' }}>
          Complete Your Free Entry
        </h1>
        <p className="text-neutral-500 text-sm mb-6">
          Tell us about your home so we can match you with the right local giveaways.
        </p>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-neutral-500 mb-2">
            <span data-testid="questionnaire-step-label" className="font-medium text-neutral-800">
              Step {step + 1} of 5: {STEP_TITLES[step]}
            </span>
            <span>{Math.round(((step + 1) / 5) * 100)}%</span>
          </div>
          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#FF3B30] rounded-full transition-all" style={{ width: `${((step + 1) / 5) * 100}%` }} />
          </div>
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>First name</Label>
                <Input data-testid="q-first-name" value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Last name</Label>
                <Input data-testid="q-last-name" value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className="mt-1 rounded-xl" />
              </div>
            </div>
            <div>
              <Label>Email address</Label>
              <Input data-testid="q-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>Mobile phone number</Label>
              <Input data-testid="q-phone" type="tel" placeholder="+1 (555) 000-0000" value={form.phone} onChange={(e) => set('phone', e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label className="mb-2 block">What is your preferred way to be contacted?</Label>
              <RadioList value={form.contact_preference} onChange={(v) => set('contact_preference', v)} options={['Email', 'Text message', 'Phone call']} testPrefix="q-contact-pref" />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>What is the address of the property you would like improved?</Label>
              <Input data-testid="q-address" placeholder="Street address" value={form.property_address} onChange={(e) => set('property_address', e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>City or town</Label>
                <Input data-testid="q-city" value={form.property_city} onChange={(e) => set('property_city', e.target.value)} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Province or state</Label>
                <Select value={form.property_region} onValueChange={(v) => set('property_region', v)}>
                  <SelectTrigger data-testid="q-region" className="mt-1 rounded-xl">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Postal / ZIP code</Label>
                <Input data-testid="q-postal" value={form.property_postal_code} onChange={(e) => set('property_postal_code', e.target.value)} className="mt-1 rounded-xl" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Do you own this property?</Label>
              <RadioList value={form.ownership_status} onChange={(v) => set('ownership_status', v)} options={OWNERSHIP} testPrefix="q-ownership" />
            </div>
            <div>
              <Label className="mb-2 block">What type of property is it?</Label>
              <RadioList value={form.property_type} onChange={(v) => set('property_type', v)} options={PROPERTY_TYPES} testPrefix="q-property-type" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <Label className="mb-1 block">Which home improvements are you most interested in?</Label>
              <p className="text-xs text-neutral-400 mb-3">Select all that apply.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {IMPROVEMENTS.map((item, i) => (
                  <label
                    key={item}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${form.improvement_interests.includes(item) ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-neutral-200 hover:border-neutral-300'}`}
                  >
                    <Checkbox
                      data-testid={`q-improvement-${i}`}
                      checked={form.improvement_interests.includes(item)}
                      onCheckedChange={() => toggleInterest(item)}
                    />
                    <span className="text-sm text-neutral-800">{item}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">When would you ideally like to complete your next home improvement project?</Label>
              <RadioList value={form.project_timeline} onChange={(v) => set('project_timeline', v)} options={TIMELINES} testPrefix="q-timeline" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <Label className="mb-2 block">Are you considering selling your home?</Label>
              <RadioList value={form.selling_intent} onChange={(v) => set('selling_intent', v)} options={INTENT_OPTIONS} testPrefix="q-selling" />
            </div>
            <div>
              <Label className="mb-2 block">Are you considering purchasing another property?</Label>
              <RadioList value={form.buying_intent} onChange={(v) => set('buying_intent', v)} options={INTENT_OPTIONS} testPrefix="q-buying" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <div>
              <Label className="mb-2 block">How did you hear about Spinser?</Label>
              <RadioList value={form.heard_about} onChange={(v) => set('heard_about', v)} options={HEARD_ABOUT} testPrefix="q-heard" />
            </div>
            <div>
              <Label>Sponsor or referral code <span className="text-neutral-400">(optional)</span></Label>
              <Input data-testid="q-sponsor-code" value={form.sponsor_code} onChange={(e) => set('sponsor_code', e.target.value)} className="mt-1 rounded-xl" />
            </div>

            <div className="space-y-3 border-t border-neutral-200 pt-5">
              <p className="text-sm font-semibold text-neutral-900">Required Confirmations</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="q-confirm-accuracy" checked={form.confirm_accuracy} onCheckedChange={(v) => set('confirm_accuracy', !!v)} className="mt-0.5" />
                <span className="text-sm text-neutral-700">
                  I confirm that the information I have provided is accurate and that I meet the eligibility requirements described in the Official Rules.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="q-agree-communications" checked={form.agree_entry_communications} onCheckedChange={(v) => set('agree_entry_communications', !!v)} className="mt-0.5" />
                <span className="text-sm text-neutral-700">
                  I agree to receive communications necessary to administer my Spinser entry, including entry confirmation, eligibility verification, winner announcements, and project-related updates.
                </span>
              </label>
            </div>

            <div className="space-y-3 border-t border-neutral-200 pt-5">
              <p className="text-sm font-semibold text-neutral-900">Optional Marketing Consent</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="q-marketing-opt-in" checked={form.marketing_opt_in} onCheckedChange={(v) => set('marketing_opt_in', !!v)} className="mt-0.5" />
                <span className="text-sm text-neutral-700">
                  I agree to receive relevant home improvement, real estate, and promotional communications from Spinser.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox data-testid="q-sponsor-sharing-opt-in" checked={form.sponsor_sharing_opt_in} onCheckedChange={(v) => set('sponsor_sharing_opt_in', !!v)} className="mt-0.5" />
                <span className="text-sm text-neutral-700">
                  I agree that my information may be shared with the participating realtor sponsor assigned to my geographic area so they may contact me about relevant real estate services. I understand that this consent is optional and is not required to enter.
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-100">
          {step > 0 ? (
            <Button data-testid="questionnaire-back-btn" variant="outline" className="rounded-xl" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : (
            <button data-testid="questionnaire-skip-btn" onClick={() => navigate('/dashboard')} className="text-sm text-neutral-400 hover:text-neutral-600 underline">
              Skip for now
            </button>
          )}
          {step < 4 ? (
            <Button
              data-testid="questionnaire-next-btn"
              disabled={!stepValid()}
              className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl px-8"
              onClick={() => setStep(step + 1)}
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="questionnaire-submit-btn"
              disabled={!stepValid() || loading}
              className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl px-8 h-12 font-medium"
              onClick={handleSubmit}
            >
              {loading ? 'Submitting...' : 'Submit My Free Entry'}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
